/**
 * Miata Registry
 * Copyright (C) 2024-2026 Matthew Congrove
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { useAuth, useUser } from '@clerk/clerk-react';
import {
	flip,
	FloatingPortal,
	offset,
	safePolygon,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useHover,
	useInteractions,
} from '@floating-ui/react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import Symbol from '../assets/symbol.svg?react';
import { Clerk } from '../components/account/Clerk';
import { useModal } from '../context/ModalContext';
import { Icon } from './Icon';

const Pending = lazy(() =>
	import('./account/Pending').then((module) => ({
		default: module.Pending,
	}))
);

interface DropdownProps {
	label: string;
	items: { label: React.ReactNode; to?: string; onClick?: () => void }[];
	isActive?: boolean;
}

const Dropdown = ({ label, items, isActive }: DropdownProps) => {
	const location = useLocation();
	const [open, setOpen] = useState(false);

	useEffect(() => {
		setOpen(false);
	}, [location.pathname]);

	const { refs, floatingStyles, context } = useFloating({
		open,
		onOpenChange: setOpen,
		placement: 'bottom-start',
		middleware: [offset(8), flip(), shift({ padding: 16 })],
	});

	const click = useClick(context);
	const hover = useHover(context, {
		handleClose: safePolygon({ blockPointerEvents: true }),
	});
	const dismiss = useDismiss(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		hover,
		dismiss,
	]);

	return (
		<>
			<button
				ref={refs.setReference}
				{...getReferenceProps()}
				type="button"
				className={`text-sm ${
					isActive
						? 'text-brg font-medium'
						: 'text-brg-mid hover:text-brg'
				} transition-colors flex items-center gap-1`}
				data-cy={`header-${label.toLowerCase()}`}
				aria-expanded={open}
			>
				{label}

				<Icon name="chevron-down" fixed className="text-xs" />
			</button>

			{open && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						{...getFloatingProps()}
						className="z-[60] p-2 w-48 bg-white rounded-lg shadow-lg border border-brg-border"
					>
						{items.map((item, index) => {
							const path = item.to?.split('#')[0];
							const matches =
								!!path &&
								(location.pathname === path ||
									location.pathname.startsWith(`${path}/`));
							// Prefer the longest matching path so /registry/editions
							// doesn't also bold /registry
							const longerMatch = items.some((other) => {
								const otherPath = other.to?.split('#')[0];
								if (
									!otherPath ||
									otherPath.length <= (path?.length ?? 0)
								) {
									return false;
								}
								return (
									location.pathname === otherPath ||
									location.pathname.startsWith(
										`${otherPath}/`
									)
								);
							});
							const itemIsActive = matches && !longerMatch;

							const className = `block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
								itemIsActive
									? 'text-brg font-medium'
									: 'text-brg-mid hover:text-brg hover:bg-brg-light'
							}`;

							return item.to ? (
								<Link
									key={item.to}
									to={item.to}
									className={className}
									data-cy={`header-${item.label?.toString().toLowerCase().replace(/\s+/g, '-')}`}
								>
									{item.label}
								</Link>
							) : (
								<button
									key={index}
									type="button"
									onClick={() => {
										item.onClick?.();
										setOpen(false);
									}}
									className={className}
									data-cy={`header-${item.label?.toString().toLowerCase().replace(/\s+/g, '-')}`}
								>
									{item.label}
								</button>
							);
						})}
					</div>
				</FloatingPortal>
			)}
		</>
	);
};

export const Header = () => {
	const { user } = useUser();
	const { getToken } = useAuth();
	const location = useLocation();
	const { openModal } = useModal();
	const [pendingChanges, setPendingChanges] = useState(null);

	useEffect(() => {
		const fetchPendingChanges = async () => {
			if (!user?.id) {
				return;
			}

			try {
				const token = await getToken();
				const response = await fetch(
					`${import.meta.env.VITE_CLOUDFLARE_WORKER_URL}/owners/${user.id}/pending`,
					{
						headers: {
							Authorization: `Bearer ${token}`,
						},
					}
				);

				const data = await response.json();

				setPendingChanges(data);
			} catch (error) {
				console.error('Failed to fetch pending changes:', error);

				setPendingChanges(null);
			}
		};

		fetchPendingChanges();
	}, [user?.id, getToken]);

	const isHomePage = location.pathname === '/';

	const isActive = (path: string) => {
		return location.pathname.startsWith(path);
	};

	const NavLinks = () => (
		<nav className="flex items-center justify-between w-full h-11">
			<div className="flex items-center gap-6">
				<Dropdown
					label="Registry"
					items={[
						{ label: 'Browse the cars', to: '/registry' },
						{
							label: 'Browse the editions',
							to: '/registry/editions',
						},
						{
							label: 'Register your Miata',
							onClick: () => openModal('register'),
						},
					]}
					isActive={isActive('/registry')}
				/>

				<Dropdown
					label="About"
					items={[
						{ label: 'About the Registry', to: '/about' },
						{
							label: (
								<span className="flex items-center gap-1.5">
									Rarity Scores{' '}
									<Icon
										name="crown"
										className="text-sm -mt-0.5 opacity-60 text-yellow-600"
									/>
								</span>
							),
							to: '/rarity',
						},
						{ label: 'News & Updates', to: '/news' },
						{ label: 'Resources', to: '/resources' },
						{ label: 'Contact us', to: '/about#contact' },
					]}
					isActive={
						isActive('/about') ||
						isActive('/news') ||
						isActive('/resources') ||
						isActive('/rarity')
					}
				/>

				{user?.publicMetadata?.moderator ? (
					<Link
						to="/moderation"
						className={twMerge(
							'hidden lg:flex text-sm text-brg-mid hover:text-brg transition-colors items-center gap-1 mr-6',
							isActive('/moderation')
								? 'text-brg font-medium'
								: 'text-brg-mid hover:text-brg'
						)}
					>
						Moderation
					</Link>
				) : null}
			</div>

			<div className="flex h-9 items-center gap-4">
				{pendingChanges && (
					<Suspense>
						<Pending changes={pendingChanges} />
					</Suspense>
				)}

				<Clerk />
			</div>
		</nav>
	);

	if (isHomePage) {
		return (
			<>
				<header className="absolute top-0 left-0 right-0 z-50 h-20">
					<div className="mx-auto py-4 px-8 lg:px-16">
						<div className="flex items-center">
							<NavLinks />
						</div>
					</div>
				</header>
			</>
		);
	}

	return (
		<>
			<header className="fixed top-0 left-0 right-0 bg-white border-b border-brg-light z-50 h-20">
				<div className="container mx-auto py-4 relative">
					<div className="flex items-center">
						<Link to="/" className="mr-8 lg:mr-12">
							<Symbol className="w-16 h-auto text-brg" />

							<span className="sr-only">Miata Registry</span>
						</Link>

						<NavLinks />
					</div>
				</div>
			</header>
		</>
	);
};
